import WorkVersion from "work/work-agreements/versions/work-version";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { ProposalBoxType, ProposalContext, ProposalOrderField } from "work/proposals/__constants/proposal-lifecycle";
import { useGetProposalsQuery } from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import { isProposalLoadIssue } from "work/proposals/_store/proposals-redux-slice";
import useStartDraftFromPreviousProposal from "work/proposals/drafts-from-previous/_hooks/use-start-draft-from-previous-proposal";
import Proposal from "work/proposals/proposal";
import { getProposalStatusLabel } from "work/proposals/proposal-status-display";

/**
 * "Create from Previous" (#1569): the Proposals table's way to start a new, unsaved draft pre-filled
 * from a proposal the reader picks here. Offered from the table alone, not from a proposal's own page
 * (#6260).
 */
export const CREATE_FROM_PREVIOUS_LABEL = "Create from Previous";
export const CREATE_FROM_PREVIOUS_TOOLTIP =
  "Start a new draft with this proposal's details, teams, fees and documents. Nothing is saved until you save it.";
export const CREATE_FROM_PREVIOUS_DIALOG_TITLE = "Create from a Previous Proposal";
export const PREVIOUS_PROPOSAL_SEARCH_LABEL = "Search proposals";
export const PREVIOUS_PROPOSAL_LIST_LABEL = "Previous proposals";

/** How many proposals one page of the picker shows. Enough to scan, few enough to fit a laptop. */
export const PICKER_PAGE_SIZE = 8;

/** Which proposals the picker lists. `undefined` is every proposal the reader can read. */
export const PICKER_FILTERS: readonly { label: string; boxType?: ProposalBoxType }[] = [
  { label: "All" },
  { label: "Active", boxType: ProposalBoxType.AllActive },
  { label: "Drafts", boxType: ProposalBoxType.Draft },
  { label: "Archived", boxType: ProposalBoxType.Archive },
];

const ResultsFrame = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  minHeight: 320,
  overflowY: "auto",
}));

const SecondaryLine = styled("span")(() => ({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

/**
 * Picks the proposal a new draft starts from (#1569).
 *
 * A firm can have hundreds of proposals, so this is a search, not a dropdown: the reader narrows it by
 * name or description and by where the proposal stands, reads enough of each row to tell two similar
 * proposals apart, and pages through the rest. Most recently updated first, because the proposal
 * somebody wants to send again is usually one they worked on lately.
 */
export default function CreateFromPreviousProposalDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const session = useSession();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const draftFromPrevious = useStartDraftFromPreviousProposal();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterIndex, setFilterIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<Proposal | null>(null);

  // Back to the first page only when the search itself changes. Firing on open as well put a page the
  // reader had already chosen back to the first one.
  useEffect(() => {
    const next = searchTerm.trim();
    if (next === debouncedSearchTerm) return;
    const id = setTimeout(() => {
      setDebouncedSearchTerm(next);
      setPageIndex(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm, debouncedSearchTerm]);

  const viewingAsVendor = session.context?.viewingAsVendor;
  const boxType = PICKER_FILTERS[filterIndex]?.boxType;
  const query = useMemo(
    () =>
      new ProposalQuery({
        filterParameters: new ProposalFilterParameters({
          query: debouncedSearchTerm || undefined,
          context: viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          boxType,
        }),
        orderParameters: new ProposalOrderParameters({
          orderBy: ProposalOrderField.Updated,
          orderDir: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({ pageIndex, pageSize: PICKER_PAGE_SIZE }),
      }),
    [boxType, debouncedSearchTerm, pageIndex, viewingAsVendor],
  );
  const proposalsQuery = useGetProposalsQuery(
    open ? { interfaceName: "createFromPreviousProposalPicker", query } : skipToken,
  );
  const currentUser = session.user;
  const proposals = useMemo<Proposal[]>(() => {
    if (!proposalsQuery.data || !currentUser) return [];
    return proposalsQuery.data.data.flatMap((json) => {
      if (isProposalLoadIssue(json)) return [];
      const proposal = Proposal.fromJSON({ currentUser, json });
      return proposal?.id ? [proposal] : [];
    });
  }, [proposalsQuery.data, currentUser]);
  const totalElements = proposalsQuery.data?.headers.totalElements ?? proposals.length;
  const pageCount = Math.max(1, Math.ceil(totalElements / PICKER_PAGE_SIZE));

  function handleClose() {
    setSelected(null);
    setSearchTerm("");
    setFilterIndex(0);
    setPageIndex(0);
    onClose();
  }

  async function handleStart() {
    if (!selected) return;
    const started = await draftFromPrevious.start(selected);
    if (started) handleClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      aria-labelledby="create-from-previous-title"
    >
      <DialogTitle id="create-from-previous-title">{CREATE_FROM_PREVIOUS_DIALOG_TITLE}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{CREATE_FROM_PREVIOUS_TOOLTIP}</DialogContentText>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
          <TextField
            label={PREVIOUS_PROPOSAL_SEARCH_LABEL}
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={{ flex: "1 1 240px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filterIndex}
            aria-label="Which proposals"
            onChange={(_event, value: number | null) => {
              if (value === null) return;
              setFilterIndex(value);
              setPageIndex(0);
            }}
          >
            {PICKER_FILTERS.map((filter, index) => (
              <ToggleButton key={filter.label} value={index}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <ResultsFrame>
          {proposalsQuery.isFetching && proposals.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={28} aria-label="Loading proposals" />
            </Box>
          ) : proposals.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              {proposalsQuery.isError ? "Could not load proposals." : "No proposals match."}
            </Typography>
          ) : (
            <List dense aria-label={PREVIOUS_PROPOSAL_LIST_LABEL} disablePadding>
              {proposals.map((proposal) => (
                <ProposalRow
                  key={proposal.id!.value}
                  proposal={proposal}
                  selected={Boolean(selected?.id?.isEqualTo(proposal.id))}
                  onSelect={() => setSelected(proposal)}
                />
              ))}
            </List>
          )}
        </ResultsFrame>
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Pagination
              count={pageCount}
              page={pageIndex + 1}
              size="small"
              onChange={(_event, page) => setPageIndex(page - 1)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected || draftFromPrevious.isStarting}
          loading={draftFromPrevious.isStarting}
          onClick={() => void handleStart()}
        >
          Start Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One proposal in the picker: its name, then where it stands, when it last moved, and what it is. */
function ProposalRow(props: { proposal: Proposal; selected: boolean; onSelect: () => void }) {
  const { proposal, selected, onSelect } = props;
  const status = getProposalStatusLabel(proposal.status);
  const updated = proposal.lastUpdated ?? proposal.createdDate;
  const facts = [status, updated ? `Updated ${updated.format("MMM D, YYYY")}` : undefined].filter(Boolean).join(" · ");
  const description = proposal.description?.value;

  return (
    <ListItemButton selected={selected} onClick={onSelect} aria-pressed={selected} divider>
      <ListItemText
        primary={WorkVersion.nameWithVersion(
          proposal.name?.value ?? "Untitled proposal",
          WorkVersion.of(proposal.workAgreement),
        )}
        slotProps={{ primary: { sx: { fontWeight: selected ? 600 : 500 } }, secondary: { component: "div" } }}
        secondary={
          <>
            <SecondaryLine>{facts}</SecondaryLine>
            {description && <SecondaryLine title={description}>{description}</SecondaryLine>}
          </>
        }
      />
    </ListItemButton>
  );
}
